import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Copy, Hash, KeyRound, Smartphone, Signal } from "lucide-react";
import { api } from "../lib/api";
import { Shell } from "../components/Shell";

export default function ManagerPortal() {
  const [sites, setSites] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [siteFilter, setSiteFilter] = useState("all");

  const load = () => {
    api.get("/manager/sites").then((res) => setSites(res.data)).catch(() => {});
    api.get("/manager/extensions").then((res) => setExtensions(res.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const visible = siteFilter === "all" ? extensions : extensions.filter((e) => e.site_id === siteFilter);
  const siteName = (id) => sites.find((s) => s.id === id)?.name || "—";
  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  return (
    <Shell title="Site Manager">
      <div className="ops-card p-5 mb-6" data-testid="manager-intro">
        <h2 className="text-xl font-bold text-slate-100 mb-1">Your Sites & Extensions</h2>
        <p className="text-sm text-slate-400">
          Read-only view. Share the SIP credentials with tenants so they can connect their app
          (GSWave / Linphone). Changes are made by the administrator.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="manager-site-filters">
        <button data-testid="manager-site-all" onClick={() => setSiteFilter("all")}
          className={`px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${
            siteFilter === "all"
              ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
              : "text-slate-400 border-[#1E2638] hover:text-slate-200"}`}>
          All sites ({extensions.length})
        </button>
        {sites.map((s) => (
          <button key={s.id} data-testid={`manager-site-${s.id}`} onClick={() => setSiteFilter(s.id)}
            className={`px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors inline-flex items-center gap-1.5 ${
              siteFilter === s.id
                ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                : "text-slate-400 border-[#1E2638] hover:text-slate-200"}`}>
            <Building2 className="w-3.5 h-3.5" />
            {s.name} ({extensions.filter((e) => e.site_id === s.id).length})
          </button>
        ))}
      </div>

      <div className="ops-card overflow-hidden" data-testid="manager-extensions-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="manager-extensions-table">
            <thead>
              <tr className="text-left ops-caption border-b border-[#1E2638] bg-[#0A0D14]">
                <th className="px-4 py-3">Ext</th>
                <th className="px-4 py-3">Label</th>
                <th className="px-4 py-3">Site</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Server</th>
                <th className="px-4 py-3">Presence</th>
                <th className="px-4 py-3">Share Credentials</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-testid="manager-empty">
                  No extensions on your sites yet.
                </td></tr>
              )}
              {visible.map((e) => (
                <tr key={e.id} className="border-b border-[#1E2638]/50 last:border-0 hover:bg-[#0A0D14]/60"
                  data-testid={`manager-ext-row-${e.id}`}>
                  <td className="px-4 py-3 font-mono font-bold text-emerald-400">
                    <span className="inline-flex items-center gap-1.5"><Hash className="w-3.5 h-3.5" />{e.number}</span>
                    {e.sip_endpoint && e.sip_endpoint !== e.number && (
                      <div className="text-[10px] font-normal text-amber-400/80 mt-0.5" data-testid={`manager-sip-username-${e.id}`}>user: {e.sip_endpoint}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-100">{e.label}</td>
                  <td className="px-4 py-3 text-slate-300 text-xs">{siteName(e.site_id)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-mono border ${
                      e.kind === "intercom"
                        ? "text-cyan-400 bg-cyan-500/10 border-cyan-500/30"
                        : "text-slate-300 bg-slate-500/10 border-slate-500/30"}`}>
                      {e.kind.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">{e.server_label}</td>
                  <td className="px-4 py-3">
                    {e.kind === "intercom" ? (
                      <span className="text-xs font-mono text-slate-500">DEVICE</span>
                    ) : (
                      <span className={`inline-flex items-center gap-1.5 text-xs font-mono ${e.online ? "text-emerald-400" : "text-slate-500"}`}
                        data-testid={`manager-presence-${e.id}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${e.online ? "bg-emerald-400 pulse-dot" : "bg-slate-600"}`} />
                        {e.online ? "ONLINE" : "OFFLINE"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {e.kind === "tenant" ? (
                        <>
                          <button data-testid={`manager-copy-user-${e.id}`} title="SIP username"
                            onClick={() => copy(`${e.sip_endpoint || e.number}@${(e.server_label || "").split(":")[0]}`, "SIP username")}
                            className="text-xs font-mono text-slate-400 hover:text-emerald-400 inline-flex items-center gap-1">
                            <Smartphone className="w-3 h-3" /> user
                          </button>
                          <button data-testid={`manager-copy-secret-${e.id}`} title="SIP password (secret)"
                            onClick={() => copy(e.sip_secret, "SIP password")}
                            className="text-xs font-mono text-slate-400 hover:text-emerald-400 inline-flex items-center gap-1">
                            <KeyRound className="w-3 h-3" /> password
                          </button>
                          <button data-testid={`manager-copy-wsecret-${e.id}`} title="Browser audio password"
                            onClick={() => copy(e.webrtc_secret || "", "Browser audio password")}
                            className="text-xs font-mono text-slate-400 hover:text-cyan-400 inline-flex items-center gap-1">
                            <Signal className="w-3 h-3" /> browser
                          </button>
                        </>
                      ) : (
                        <button data-testid={`manager-copy-apikey-${e.id}`} title="Device API key"
                          onClick={() => copy(e.api_key || "", "Device API key")}
                          className="text-xs font-mono text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1">
                          <Copy className="w-3 h-3" /> api key
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Shell>
  );
}
